/**
 * Stat card component for displaying a single metric value.
 * Used in the monitoring dashboard for KPI cards.
 *
 * @module components/ui/StatCard
 */

import { ReactNode } from 'react';
import { clsx } from 'clsx';
import { Card } from './Card';

export interface StatCardProps {
  title: string;
  value: string;
  suffix?: string;
  icon?: ReactNode;
  status?: 'success' | 'warning' | 'danger' | 'neutral';
  loading?: boolean;
}

const statusStyles = {
  success: 'border-green-200 dark:border-green-900/50 bg-green-50/50 dark:bg-green-950/20',
  warning: 'border-yellow-200 dark:border-yellow-900/50 bg-yellow-50/50 dark:bg-yellow-950/20',
  danger: 'border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20',
  neutral: '',
};

const valueStyles = {
  success: 'text-green-700 dark:text-green-400',
  warning: 'text-yellow-700 dark:text-yellow-400',
  danger: 'text-red-700 dark:text-red-400',
  neutral: 'text-gray-900 dark:text-white',
};

export function StatCard({
  title,
  value,
  suffix,
  icon,
  status = 'neutral',
  loading,
}: StatCardProps) {
  if (loading) {
    return (
      <Card className="p-4">
        <div className="animate-pulse">
          <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded-sm mb-3" />
          <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded-sm" />
        </div>
      </Card>
    );
  }

  return (
    <Card className={clsx('p-4', statusStyles[status])}>
      <div className="flex items-center gap-2 mb-1">
        {icon && <span className="text-gray-400 dark:text-gray-500">{icon}</span>}
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {title}
        </p>
      </div>
      <p className={clsx('text-2xl font-bold tabular-nums', valueStyles[status])}>
        {value}
        {suffix && (
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">
            {suffix}
          </span>
        )}
      </p>
    </Card>
  );
}
